// packages/web/src/components/layout/layouts/AnonymousMarketplaceLayout.tsx
'use client';

import MenuIcon from '@mui/icons-material/Menu';
import {
  Box,
  Button,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';

import { CartAuthModal } from '../../auth/CartAuthModal';
import SlideDownLogin from '../../auth/SlideDownLogin';
import SlideDownRegister from '../../auth/SlideDownRegister';
import DeploymentIndicator from '../DeploymentIndicator';
import { Footer } from '../Footer';
import HeaderBranding from '../HeaderBranding';
import { BaseHeader } from '../headers/BaseHeader';
import MarketplaceHeaderElements from '../MarketplaceHeaderElements';

interface AnonymousMarketplaceLayoutProps {
  children: React.ReactNode;
  catalogBranding?: {
    name?: string;
    logo?: string;
    primaryColor?: string;
    website?: string;
    headerText?: string;
  };
}

const PRIMARY_LINKS = [
  { label: 'Browse', href: '/' },
  { label: 'For Buyers', href: '/for-buyers' },
  { label: 'For Partners & Suppliers', href: '/for-partners' },
  { label: 'For AI Agents', href: '/for-agents' },
  { label: 'How It Works', href: '/how-aithon-works' },
];

export const AnonymousMarketplaceLayout: React.FC<AnonymousMarketplaceLayoutProps> = ({
  children,
  catalogBranding,
}) => {
  const router = useRouter();
  const [showCartAuthModal, setShowCartAuthModal] = React.useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showLoginForm, setShowLoginForm] = React.useState(false);
  const [showRegisterForm, setShowRegisterForm] = React.useState(false);
  const [headerText, setHeaderText] = useState('');

  const isCatalogPage = router.pathname.startsWith('/c/');
  const catalogSlug = isCatalogPage ? (router.query.catalogInstance as string) : null;

  useEffect(() => {
    if (catalogBranding?.headerText && catalogBranding.headerText.trim()) {
      setHeaderText(catalogBranding.headerText.trim());
    } else if (!catalogSlug) {
      setHeaderText('');
    }
  }, [catalogBranding, catalogSlug]);

  const homeHref = catalogSlug ? `/c/${catalogSlug}` : '/';
  const authRegisterHref = catalogSlug ? `/c/${catalogSlug}/register` : '/register';

  const branding = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, transition: 'all 0.2s ease' }}>
      <HeaderBranding
        size={isMobile ? 'large' : 'xlarge'}
        clickable={true}
        href={homeHref}
        branding={catalogBranding || null}
        showName={false}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '24px' }}>
        <Box sx={{ color: 'text.secondary', fontSize: '0.875rem', fontWeight: 500, minHeight: '20px' }}>
          {headerText}
        </Box>
      </Box>
    </Box>
  );

  const actions = (
    <>
      <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1, ml: 2, flexWrap: 'wrap' }}>
        {!isCatalogPage && PRIMARY_LINKS.map((link) => (
          <Button
            key={link.href}
            color="inherit"
            onClick={() => router.push(link.href)}
            sx={{
              fontSize: '0.82rem',
              opacity: router.pathname === link.href ? 1 : 0.72,
              fontWeight: router.pathname === link.href ? 700 : 500,
              textTransform: 'none',
              '&:hover': { opacity: 1 },
            }}
          >
            {link.label}
          </Button>
        ))}
        <Button color="inherit" variant="outlined" onClick={() => router.push(authRegisterHref)}>
          Register
        </Button>
        <Button color="inherit" onClick={() => setShowLoginForm(true)}>
          Login
        </Button>
      </Box>

      <IconButton
        color="inherit"
        onClick={() => setMobileMenuOpen(true)}
        sx={{ display: { xs: 'flex', md: 'none' }, ml: 1 }}
        aria-label="menu"
      >
        <MenuIcon />
      </IconButton>
    </>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <BaseHeader
        branding={branding}
        actions={actions}
        catalogBranding={catalogBranding || null}
        sx={{ '& .MuiToolbar-root': { minHeight: { xs: '56px', sm: '88px' } } }}
      >
        <MarketplaceHeaderElements />
      </BaseHeader>

      <Box component="main" sx={{ flexGrow: 1, px: 3, py: 0, width: '100%' }}>
        <Box sx={{ minHeight: { xs: '56px', sm: '88px' } }} />
        {children}
      </Box>

      {!isCatalogPage && <Footer onPartnerLoginClick={() => setShowLoginForm(true)} />}
      <DeploymentIndicator />

      <Drawer anchor="right" open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)}>
        <Box sx={{ width: 280, pt: 2 }}>
          <List>
            {!isCatalogPage && PRIMARY_LINKS.map((link) => (
              <ListItem key={link.href} disablePadding>
                <ListItemButton onClick={() => { setMobileMenuOpen(false); router.push(link.href); }}>
                  <ListItemText primary={link.label} primaryTypographyProps={{ fontWeight: router.pathname === link.href ? 700 : 500 }} />
                </ListItemButton>
              </ListItem>
            ))}
            <ListItem disablePadding>
              <ListItemButton onClick={() => { setMobileMenuOpen(false); router.push(authRegisterHref); }}>
                <ListItemText primary="Register" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={() => { setMobileMenuOpen(false); setShowLoginForm(true); }}>
                <ListItemText primary="Login" primaryTypographyProps={{ fontWeight: 600 }} />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Drawer>

      <SlideDownLogin
        open={showLoginForm}
        onClose={() => setShowLoginForm(false)}
        onSuccess={() => setShowLoginForm(false)}
        onSwitchToRegister={() => {
          setShowLoginForm(false);
          router.push(authRegisterHref);
        }}
        catalogSlug={catalogSlug}
      />

      <SlideDownRegister
        open={showRegisterForm}
        onClose={() => setShowRegisterForm(false)}
        onSuccess={() => setShowRegisterForm(false)}
        onSwitchToLogin={() => {
          setShowRegisterForm(false);
          setShowLoginForm(true);
        }}
        catalogSlug={catalogSlug}
      />

      <CartAuthModal
        open={showCartAuthModal}
        onClose={() => setShowCartAuthModal(false)}
        onSuccess={() => {
          setShowCartAuthModal(false);
        }}
        returnCatalog={catalogSlug}
      />
    </Box>
  );
};
